import { useState, useEffect, useCallback } from "react";
import { createStatefulContext } from "../../../../contexts/createStatefulContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import {
  getCollections,
  searchCollections as apiSearchCollections,
  type CollectionType,
  type CollectionFilterBy,
  type Collection,
  type PaginationData,
} from "../../../../apis/bacchus/collections";
import type { TableSortConfig, SortableTableField } from "../types";
import { DEFAULT_PAGE_SIZE } from "../constants";
import { formatRelativeTime } from "../utils/formatRelativeTime";

export const {
  WithCollectionsContext,
  collectionsContext,
  useCollectionsContext,
  useCollectionsContextFactory,
} = createStatefulContext("collections", () => {
  // Core data state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(false);

  // Table-specific state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<CollectionType | "all">("all");
  const [filterBy, setFilterBy] = useState<CollectionFilterBy>("owned");
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | null>(null);

  // Debounced search query for automatic API requests
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const loadCollections = useCallback(async () => {
    if (debouncedSearchQuery.trim()) {
      return;
    }

    try {
      setLoading(true);

      const sortOptions = sortConfig
        ? {
            orderBy: sortConfig.field,
            orderDirections: sortConfig.direction,
          }
        : {};

      const result = await getCollections(typeFilter, {
        page: page + 1,
        pageSize: rowsPerPage,
        filterBy,
        ...sortOptions,
      });

      const { data, pagination: paginationData } = result;

      if (data) {
        setCollections(data);
        setPagination(paginationData || null);
      }
    } catch (error) {
      console.error("Failed to load collections:", error);
      setLoadingError(true);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchQuery,
    filterBy,
    typeFilter,
    page,
    rowsPerPage,
    sortConfig,
  ]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const searchCollections = useCallback(async () => {
    if (!debouncedSearchQuery.trim()) {
      return;
    }

    try {
      setLoading(true);

      const sortOptions = sortConfig
        ? {
            orderBy: sortConfig.field,
            orderDirections: sortConfig.direction,
          }
        : {};

      const { data, pagination: paginationData } = await apiSearchCollections(
        typeFilter,
        {
          search: debouncedSearchQuery,
          page: page + 1,
          pageSize: rowsPerPage,
          ...sortOptions,
        }
      );

      if (data) {
        setCollections(data);
        setPagination(paginationData || null);
      }

      setLoading(false);
    } catch {
      setLoadingError(true);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, sortConfig, typeFilter, page, rowsPerPage]);

  useEffect(() => {
    searchCollections();
  }, [searchCollections]);

  const refreshCollections = useCallback(() => {
    if (debouncedSearchQuery.trim()) {
      searchCollections();
    } else {
      loadCollections();
    }
  }, [debouncedSearchQuery, loadCollections, searchCollections]);

  const sortByColumn = useCallback(
    (field: SortableTableField) => {
      const newDirection =
        sortConfig?.field === field && sortConfig?.direction === "asc"
          ? "desc"
          : "asc";

      setSortConfig({ field, direction: newDirection });
    },
    [sortConfig]
  );

  const changePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const changeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    },
    []
  );

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setTypeFilter("all");
    setSortConfig(null);
    setPage(0);
  }, []);

  const changeFilterBy = useCallback((newFilterBy: CollectionFilterBy) => {
    setFilterBy(newFilterBy);
    setPage(0);
  }, []);

  const getFormattedCollections = useCallback(() => {
    return collections.map((collection) => ({
      ...collection,
      formattedUpdatedAt: formatRelativeTime(collection.updated_at),
    }));
  }, [collections]);

  return {
    // Data
    collections: getFormattedCollections(),
    loading,
    loadingError,
    pagination,

    // Table state - React pattern [value, setValue]
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchQuery,
    setSearchQuery,
    typeFilter,
    setTypeFilter,
    filterBy,
    setFilterBy,
    sortConfig,
    setSortConfig,

    // Actions
    refreshCollections,
    loadCollections,
    sortByColumn,
    changePage,
    changeRowsPerPage,
    clearFilters,
    changeFilterBy,

    // Utils
    formatRelativeTime,

    // Setters
    setCollections,
    setLoading,
    setLoadingError,
  };
});
